import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { FileText, Calendar, MessageSquare } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useDailyReports, useWeeklySummaries, ReportFilters } from '@/features/reports/hooks/use-reports'
import { ReportCard } from '@/features/reports/components/ReportCard'
import { SummaryCard } from '@/features/reports/components/SummaryCard'
import { ReportsFiltersComponent } from '@/features/reports/components/ReportsFilters'

export const Route = createFileRoute('/_authenticated/reports')({
  component: ReportsPage,
})

function ReportsPage() {
  useDocumentTitle('Relatos')
  const [activeTab, setActiveTab] = useState<'daily' | 'weekly'>('daily')
  const [filters, setFilters] = useState<ReportFilters>({ period: 'week' })

  const { data: dailyReports, isLoading: dailyLoading } = useDailyReports(filters)
  const { data: weeklySummaries, isLoading: weeklyLoading } = useWeeklySummaries(filters)

  return (
    <MainLayout title="Relatos" subtitle="Visualize os relatos recebidos das familias">
      <div className="space-y-6">
        {/* Header com contador */}
        <div className="flex items-center gap-4">
          <div className="p-2.5 bg-teal-50 rounded-lg">
            <MessageSquare className="h-5 w-5 text-teal-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">
              {activeTab === 'daily'
                ? `${dailyReports?.length ?? 0} relatos diarios`
                : `${weeklySummaries?.length ?? 0} análises semanais`}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="card p-1 inline-flex gap-1">
          <button
            onClick={() => setActiveTab('daily')}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'daily'
                ? 'bg-teal-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FileText className="h-4 w-4" />
            Relatos Diários
          </button>
          <button
            onClick={() => setActiveTab('weekly')}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'weekly'
                ? 'bg-teal-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Calendar className="h-4 w-4" />
            Análises Semanais
          </button>
        </div>

        {/* Filtros */}
        <div className="card p-4">
          <ReportsFiltersComponent filters={filters} onFiltersChange={setFilters} />
        </div>

        {/* Conteudo */}
        {activeTab === 'daily' ? (
          dailyLoading ? (
            <ReportsSkeleton />
          ) : dailyReports && dailyReports.length > 0 ? (
            <div className="space-y-4">
              {dailyReports.map((report) => (
                <ReportCard key={report.id} report={report} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={FileText}
              title="Nenhum relato encontrado"
              description="Os relatos aparecerao aqui quando forem enviados via WhatsApp"
            />
          )
        ) : weeklyLoading ? (
          <ReportsSkeleton />
        ) : weeklySummaries && weeklySummaries.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {weeklySummaries.map((summary) => (
              <SummaryCard key={summary.id} summary={summary} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Calendar}
            title="Nenhuma análise semanal encontrada"
            description="As análises semanais sao geradas automaticamente"
          />
        )}
      </div>
    </MainLayout>
  )
}

function ReportsSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="card p-4">
          <Skeleton className="h-32 w-full" />
        </div>
      ))}
    </div>
  )
}

function EmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType
  title: string
  description: string
}) {
  return (
    <div className="card p-12 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-4">
        <Icon className="h-6 w-6 text-gray-400" />
      </div>
      <p className="text-gray-600 font-medium">{title}</p>
      <p className="text-sm text-gray-500 mt-1">{description}</p>
    </div>
  )
}
